import { useState } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { DocumentLoadStatus, type DocumentLoad } from "@/types";
import { SideDrawer } from "@/components/common/SideDrawer";
import { DATE_FORMATS, formatDate } from "@/utils/formatters/dateFormatter";
import { STATUS_CONFIG } from "./config";
import { LoadDetailsDialog } from "./LoadDetailsDialog";

/** Width of the previous-loads drawer, in pixels. Same as the details
 *  drawer so the two stack cleanly side-by-side without one looking
 *  visually out of place. Exported for `DocumentShow`'s width math. */
export const PREVIOUS_LOADS_DRAWER_WIDTH = 360;

interface PreviousLoadsDrawerProps {
  /** Loads other than the most recent, in reverse-chronological order. */
  previousLoads: DocumentLoad[];
  open: boolean;
  onClose: () => void;
  /** Offset from the right edge so this drawer can stack to the LEFT of the
   *  always-on details drawer when both are open. */
  offsetRight?: number;
}

/**
 * Side drawer listing prior `DocumentLoad` records for the document.
 *
 * Each entry summarizes one historical load: status chip, timestamp, and
 * record counts (loaded / failed). Clicking a row opens `LoadDetailsDialog`
 * with the full per-schema breakdown and failure messages — replacing the
 * legacy dead-end "2 previous loads available" string with a real
 * navigation path.
 *
 * Renders even when `previousLoads` is empty so the open/close state can be
 * driven without conditional mounting; the body shows an empty-state message.
 */
export const PreviousLoadsDrawer = ({ previousLoads, open, onClose, offsetRight = 0 }: PreviousLoadsDrawerProps) => {
  const [selectedLoad, setSelectedLoad] = useState<DocumentLoad | null>(null);

  return (
    <>
      <SideDrawer
        title="Previous loads"
        open={open}
        onClose={onClose}
        width={PREVIOUS_LOADS_DRAWER_WIDTH}
        offsetRight={offsetRight}
        hideHandle
      >
        {previousLoads.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
            <Typography variant="body2">No previous loads.</Typography>
          </Box>
        ) : (
          <Stack spacing={1.5} sx={{ pb: 3 }}>
            {previousLoads.map((load) => (
              <PreviousLoadRow key={load.id} load={load} onClick={() => setSelectedLoad(load)} />
            ))}
          </Stack>
        )}
      </SideDrawer>
      <LoadDetailsDialog load={selectedLoad} open={selectedLoad !== null} onClose={() => setSelectedLoad(null)} />
    </>
  );
};

interface PreviousLoadRowProps {
  load: DocumentLoad;
  onClick: () => void;
}

const PreviousLoadRow = ({ load, onClick }: PreviousLoadRowProps) => {
  // Guard against unknown statuses from the backend so a single bad row
  // doesn't blank the whole drawer. Same pattern in LoadDetailsDialog.
  const config = STATUS_CONFIG[load.status] ?? STATUS_CONFIG[DocumentLoadStatus.FAILED];
  const loaded = load.loaded_records?.length ?? 0;
  const failed = load.failed_records?.length ?? 0;
  const timestamp = load.created_datetime
    ? formatDate(load.created_datetime, DATE_FORMATS.DISPLAY_LONG_WITH_TIME)
    : "Unknown timestamp";

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        width: "100%",
        textAlign: "left",
        py: 1.5,
        px: 1.5,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "transparent",
        cursor: "pointer",
        transition: "background-color 120ms ease, border-color 120ms ease",
        "&:hover": {
          bgcolor: "action.hover",
          borderColor: "text.disabled",
        },
        "&:focus-visible": {
          outline: 2,
          outlineColor: "primary.main",
          outlineOffset: -2,
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
        <Chip label={config.label} size="small" color={config.color} variant="outlined" sx={{ fontWeight: 500 }} />
        <Typography variant="caption" color="text.secondary">
          {timestamp}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="body2">
          {loaded} loaded{failed > 0 ? `, ${failed} failed` : ""}
        </Typography>
        <ChevronRightIcon sx={{ fontSize: 18, color: "text.disabled" }} />
      </Stack>
    </Box>
  );
};
